import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Scan, FileArrowUp, FloppyDisk, Trash } from "@phosphor-icons/react";

const NONE = "__none__";

export default function AvizeOCR() {
  const [projects, setProjects] = useState([]);
  const [avize, setAvize] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [assigning, setAssigning] = useState({}); // {`${aviz_id}:${idx}`: project_id}
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null); // {storage_path, content_type, supplier, buyer, aviz_number, aviz_date, lines:[]}
  const fileRef = useRef(null);

  const loadLists = () => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/avize").then((r) => setAvize(r.data)).catch(() => {});
    api.get("/avize/unassigned").then((r) => setUnassigned(r.data)).catch(() => {});
  };
  useEffect(() => { loadLists(); }, []);

  const assignLine = async (row) => {
    const key = `${row.aviz_id}:${row.line_index}`;
    const pid = assigning[key];
    if (!pid || pid === NONE) { toast.error("Alege un proiect"); return; }
    try {
      const { data } = await api.post(`/avize/${row.aviz_id}/assign`, { line_index: row.line_index, project_id: pid });
      toast.success(`Reper alocat la ${data.project_code || "proiect"} — atașat în dosarul digital`);
      setAssigning((a) => { const n = { ...a }; delete n[key]; return n; });
      loadLists();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approveGeneral = async (row) => {
    if (!window.confirm(`Adaugi „${row.product_name}" (${row.quantity} ${row.unit || "buc"}) în Gestiunea generală?`)) return;
    try {
      await api.post(`/avize/${row.aviz_id}/approve-general`, { line_index: row.line_index });
      toast.success("Adăugat în Gestiunea generală");
      loadLists();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const matchProject = (code) => {
    if (!code) return NONE;
    const norm = code.replace(/[\s-]/g, "").toUpperCase();
    const hit = projects.find((p) => (p.code || "").replace(/[\s-]/g, "").toUpperCase() === norm);
    return hit ? hit.id : NONE;
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/avize/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const lines = (data.lines || []).map((l) => ({ ...l, project_id: l.project_id || matchProject(l.project_code) }));
      setDoc({ ...data, lines });
      toast.success(`Aviz citit — ${lines.length} linii extrase. Verifică și atribuie proiectele.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setLine = (i, key, val) => setDoc((d) => ({ ...d, lines: d.lines.map((l, idx) => idx === i ? { ...l, [key]: val } : l) }));
  const setHead = (key, val) => setDoc((d) => ({ ...d, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        storage_path: doc.storage_path, content_type: doc.content_type,
        supplier: doc.supplier, buyer: doc.buyer, aviz_number: doc.aviz_number, aviz_date: doc.aviz_date,
        lines: doc.lines.map((l) => ({
          nr_crt: l.nr_crt, product_name: l.product_name, unit: l.unit,
          quantity: l.quantity, project_code: l.project_code,
          project_id: l.project_id === NONE ? null : l.project_id,
        })),
      };
      const { data } = await api.post("/avize", payload);
      toast.success(`Aviz salvat — atașat la ${data.attached_projects || 0} proiect(e) în dosarul digital`);
      setDoc(null);
      loadLists();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-border bg-white/[0.02] p-4" data-testid="avize-ocr">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><Scan size={18} className="text-primary" /> Scanare avize (OCR)</h2>
        <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={onPick} data-testid="avize-file-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="avize-scan-btn">
          <FileArrowUp size={16} className="mr-1.5" /> {busy ? "Se citește avizul…" : "Încarcă & scanează aviz"}
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Acceptă <b>poze</b> (JPG/PNG) și <b>PDF-uri</b> (chiar cu mai multe pagini). Un aviz poate avea linii pentru proiecte diferite — AI-ul propune codul, tu confirmi în tabel. Liniile fără proiect din aplicație le lași pe „— fără proiect —".</p>

      {doc && (
        <div className="mt-4 space-y-3" data-testid="avize-editor">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="grid gap-1"><Label className="text-xs">Nr. aviz</Label><Input value={doc.aviz_number} onChange={(e) => setHead("aviz_number", e.target.value)} data-testid="aviz-number" /></div>
            <div className="grid gap-1"><Label className="text-xs">Dată</Label><Input value={doc.aviz_date} onChange={(e) => setHead("aviz_date", e.target.value)} data-testid="aviz-date" /></div>
            <div className="grid gap-1"><Label className="text-xs">Furnizor</Label><Input value={doc.supplier} onChange={(e) => setHead("supplier", e.target.value)} /></div>
            <div className="grid gap-1"><Label className="text-xs">Cumpărător</Label><Input value={doc.buyer} onChange={(e) => setHead("buyer", e.target.value)} /></div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Denumire produs</th>
                  <th className="px-2 py-2 text-left">UM</th>
                  <th className="px-2 py-2 text-left">Cant.</th>
                  <th className="px-2 py-2 text-left">Cod detectat</th>
                  <th className="px-2 py-2 text-left">Proiect</th>
                </tr>
              </thead>
              <tbody>
                {doc.lines.map((l, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`aviz-line-${i}`}>
                    <td className="px-2 py-1.5 text-muted-foreground">{l.nr_crt}</td>
                    <td className="px-2 py-1.5"><Input className="h-8" value={l.product_name} onChange={(e) => setLine(i, "product_name", e.target.value)} /></td>
                    <td className="px-2 py-1.5"><Input className="h-8 w-16" value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} /></td>
                    <td className="px-2 py-1.5"><Input className="h-8 w-20" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></td>
                    <td className="px-2 py-1.5 font-mono text-xs text-amber-400">{l.project_code || "—"}</td>
                    <td className="px-2 py-1.5">
                      <Select value={l.project_id} onValueChange={(v) => setLine(i, "project_id", v)}>
                        <SelectTrigger className="h-8 w-48" data-testid={`aviz-line-project-${i}`}><SelectValue placeholder="Alege…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— fără proiect —</SelectItem>
                          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDoc(null)}>Anulează</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="aviz-save-btn"><FloppyDisk size={16} className="mr-1.5" /> {saving ? "Se salvează…" : "Salvează avizul"}</Button>
          </div>
        </div>
      )}

      {unassigned.length > 0 && (
        <div className="mt-5" data-testid="avize-unassigned">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-400">Repere fără proiect ({unassigned.length})</h3>
          <p className="mb-2 text-xs text-muted-foreground">Linii de aviz care nu au (încă) un proiect în aplicație. Alocă-le unui proiect sau, dacă sunt materiale de stoc, adaugă-le MANUAL în Gestiunea generală (nu se adaugă automat).</p>
          <div className="overflow-x-auto rounded-lg border border-amber-500/30">
            <table className="w-full text-sm">
              <thead className="bg-amber-500/[0.06] text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left">Produs</th>
                  <th className="px-2 py-2 text-left">Cant.</th>
                  <th className="px-2 py-2 text-left">Cod detectat</th>
                  <th className="px-2 py-2 text-left">Aviz</th>
                  <th className="px-2 py-2 text-left">Alocă la proiect</th>
                </tr>
              </thead>
              <tbody>
                {unassigned.map((row) => {
                  const key = `${row.aviz_id}:${row.line_index}`;
                  return (
                    <tr key={key} className="border-t border-border" data-testid={`unassigned-row-${row.aviz_id}-${row.line_index}`}>
                      <td className="px-2 py-1.5">{row.product_name}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">{row.quantity} {row.unit}</td>
                      <td className="px-2 py-1.5 font-mono text-xs text-amber-400">{row.project_code || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs text-muted-foreground">{row.aviz_number || "—"} · {row.aviz_date || ""}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <Select value={assigning[key] || undefined} onValueChange={(v) => setAssigning((a) => ({ ...a, [key]: v }))}>
                            <SelectTrigger className="h-8 w-48" data-testid={`unassigned-project-${row.aviz_id}-${row.line_index}`}><SelectValue placeholder="Alege proiectul…" /></SelectTrigger>
                            <SelectContent>
                              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="secondary" onClick={() => assignLine(row)} data-testid={`unassigned-assign-${row.aviz_id}-${row.line_index}`}>Alocă</Button>
                          <Button size="sm" variant="outline" onClick={() => approveGeneral(row)} data-testid={`unassigned-approve-general-${row.aviz_id}-${row.line_index}`}>+ Gestiune</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {avize.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Avize salvate</h3>
          <div className="space-y-1.5">
            {avize.map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`aviz-item-${a.id}`}>
                <span className="font-semibold">Aviz {a.aviz_number || a.id.slice(0, 6)}</span>
                <span className="text-xs text-muted-foreground">{a.aviz_date || fmtDate(a.created_at)} · {(a.lines || []).length} linii</span>
                {a.storage_path && <a href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="ml-auto text-xs text-primary hover:underline">Vezi documentul</a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
